import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../store";
import { clearShopInfoState } from "../../slices/ShopInfo/reducer";
import { getShopInfo, updateShopInfo } from "../../slices/ShopInfo/thunk";
import { getPrimaryShopId } from "../../helpers/shop";
import userAvatar from "../../assets/img/core-img/logo-small.png";
import ConfirmModal from "../../Components/Common/ConfirmModal";
import useGeolocation from "../../hooks/useGeolocation";
import HeaderSidebarToggle from "../../Components/Common/HeaderSidebarToggle";

const ShopEditInfo = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const authUser = useSelector((state: any) => state.auth?.user);
    const { shop, loading, updating, updateError, updateSuccess } = useSelector((state: any) => state.ShopInfo);
    const shopId = useMemo(() => getPrimaryShopId(authUser), [authUser]);

    const [formState, setFormState] = useState({
        name: "",
        phone: "",
        address: "",
        tone_color: "#2563EB",
        latitude: "",
        longitude: "",
        altitude: "",
        max_qr_distance_meters: "80",
    });
    const [selectedImage, setSelectedImage] = useState<File | null>(null);
    const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);
    const [imageError, setImageError] = useState<string | null>(null);
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const { position, loading: locating, error: geolocationError, requestCurrentLocation } = useGeolocation();

    useEffect(() => {
        if (shopId && !shop) {
            dispatch(getShopInfo(shopId));
        }
    }, [dispatch, shopId, shop]);

    useEffect(() => {
        if (shop) {
            setFormState({
                name: shop?.name ?? "",
                phone: shop?.phone ?? "",
                address: shop?.address ?? "",
                tone_color: shop?.tone_color ?? shop?.toneColor ?? "#2563EB",
                latitude: shop?.latitude?.toString() ?? "",
                longitude: shop?.longitude?.toString() ?? "",
                altitude: shop?.altitude?.toString() ?? "",
                max_qr_distance_meters: (shop?.max_qr_distance_meters ?? shop?.maxQrDistanceMeters ?? 80).toString(),
            });
        }
    }, [shop]);

    useEffect(() => {
        if (updateSuccess) {
            dispatch(clearShopInfoState());
            navigate("/shop/info");
        }
    }, [dispatch, navigate, updateSuccess]);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        setFormState((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!shopId) {
            return;
        }
        setIsConfirmOpen(true);
    };

    const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0] ?? null;

        if (!file) {
            setSelectedImage(null);
            setImageError(null);
            return;
        }

        const maxSizeInBytes = 1024 * 1024;
        if (file.size > maxSizeInBytes) {
            setSelectedImage(null);
            setImageError("Ảnh đại diện phải nhỏ hơn hoặc bằng 1MB.");
            event.target.value = "";
            return;
        }

        setImageError(null);
        setSelectedImage(file);
    };

    useEffect(() => {
        if (!selectedImage) {
            setPreviewImageUrl(null);
            return;
        }

        const objectUrl = URL.createObjectURL(selectedImage);
        setPreviewImageUrl(objectUrl);

        return () => {
            URL.revokeObjectURL(objectUrl);
        };
    }, [selectedImage]);

    useEffect(() => {
        if (position.latitude === null || position.longitude === null) {
            return;
        }

        setFormState((prev) => ({
            ...prev,
            latitude: position.latitude !== null ? position.latitude.toFixed(7) : prev.latitude,
            longitude: position.longitude !== null ? position.longitude.toFixed(7) : prev.longitude,
            altitude: position.altitude !== null ? position.altitude.toFixed(2) : prev.altitude,
        }));
    }, [position.altitude, position.latitude, position.longitude]);

    const avatarUrl = previewImageUrl || shop?.image || shop?.avatar || userAvatar;
    const handleConfirmUpdate = () => {
        if (!shopId) {
            setIsConfirmOpen(false);
            return;
        }

        const payload = new FormData();
        payload.append("name", formState.name);
        payload.append("phone", formState.phone);
        payload.append("address", formState.address);
        payload.append("tone_color", formState.tone_color);
        payload.append("latitude", formState.latitude);
        payload.append("longitude", formState.longitude);
        payload.append("altitude", formState.altitude);
        payload.append("max_qr_distance_meters", formState.max_qr_distance_meters);
        if (selectedImage) {
            payload.append("image", selectedImage);
        }

        dispatch(updateShopInfo({ shopId, data: payload }));
        setIsConfirmOpen(false);
    };

    return (
        <React.Fragment>
            <div className="header-area" id="headerArea">
                <div className="container h-100 d-flex align-items-center justify-content-between rtl-flex-d-row-r">
                    <div className="back-button me-2">
                        <Link to="/shop/info">
                            <i className="mdi mdi-arrow-left"></i>
                        </Link>
                    </div>
                    <div className="page-heading">
                        <h6 className="mb-0">Chỉnh sửa thông tin quán</h6>
                    </div>
                    <HeaderSidebarToggle className="ms-2" />
                </div>
            </div>

            <div className="page-content-wrapper">
                <div className="container">
                    <div className="profile-wrapper-area py-3">
                        <div className="card user-info-card">
                            <div className="card-body p-4 d-flex align-items-center">
                                <div className="user-profile me-3">
                                    <img src={avatarUrl} alt={shop?.name ?? "Shop"} />
                                    <div className="change-user-thumb">
                                        <form>
                                            <input
                                                className="form-control-file"
                                                type="file"
                                                accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
                                                onChange={handleImageChange}
                                            />
                                            <button type="button">
                                                <i className="mdi mdi-pencil"></i>
                                            </button>
                                        </form>
                                    </div>
                                </div>
                                <div className="user-info">
                                    {/*<p className="mb-0 text-white">*/}
                                    {/*    {shop?.slug ? `@${shop.slug}` : "@"}*/}
                                    {/*</p>*/}
                                    <h5 className="mb-0 text-white">{shop?.name ?? "Thông tin quán"}</h5>
                                </div>
                            </div>
                        </div>

                        <div className="card user-data-card">
                            <div className="card-body">
                                {loading && <p className="text-center mb-3">Đang tải thông tin...</p>}
                                {updateError && <p className="text-danger mb-3">{updateError}</p>}
                                {imageError && <p className="text-danger mb-3">{imageError}</p>}
                                <form onSubmit={handleSubmit}>
                                    <div className="mb-3">
                                        <div className="title mb-2">
                                            <i className="mdi mdi-storefront-outline"></i>
                                            <span>Tên quán</span>
                                        </div>
                                        <input
                                            className="form-control"
                                            type="text"
                                            name="name"
                                            value={formState.name}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <div className="title mb-2">
                                            <i className="mdi mdi-phone"></i>
                                            <span>Điện thoại</span>
                                        </div>
                                        <input
                                            className="form-control"
                                            type="text"
                                            name="phone"
                                            value={formState.phone}
                                            onChange={handleChange}
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <div className="title mb-2">
                                            <i className="mdi mdi-map-marker"></i>
                                            <span>Địa chỉ</span>
                                        </div>
                                        <input
                                            className="form-control"
                                            type="text"
                                            name="address"
                                            value={formState.address}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <div className="d-flex align-items-center justify-content-between mb-2">
                                            <div className="title mb-0">
                                                <i className="mdi mdi-crosshairs-gps"></i>
                                                <span>Vị trí quán</span>
                                            </div>
                                            <button type="button" className="btn btn-sm btn-outline-primary" onClick={requestCurrentLocation}>
                                                {locating ? "Đang lấy GPS..." : "Lấy vị trí hiện tại"}
                                            </button>
                                        </div>
                                        {geolocationError && <small className="text-danger d-block mb-2">{geolocationError}</small>}
                                        <div className="row g-2">
                                            <div className="col-12 col-md-4">
                                                <input className="form-control" type="number" step="0.0000001" name="latitude" value={formState.latitude} onChange={handleChange} placeholder="Latitude" />
                                            </div>
                                            <div className="col-12 col-md-4">
                                                <input className="form-control" type="number" step="0.0000001" name="longitude" value={formState.longitude} onChange={handleChange} placeholder="Longitude" />
                                            </div>
                                            <div className="col-12 col-md-4">
                                                <input className="form-control" type="number" step="0.01" name="altitude" value={formState.altitude} onChange={handleChange} placeholder="Altitude" />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="mb-3">
                                        <div className="title mb-2">
                                            <i className="mdi mdi-map-marker-distance"></i>
                                            <span>Khoảng cách QR tối đa (m)</span>
                                        </div>
                                        <input
                                            className="form-control"
                                            type="number"
                                            min="1"
                                            max="10000"
                                            step="1"
                                            name="max_qr_distance_meters"
                                            value={formState.max_qr_distance_meters}
                                            onChange={handleChange}
                                            required
                                        />
                                        <small className="text-muted">Khách chỉ đặt món/gọi phục vụ khi GPS nằm trong phạm vi này.</small>
                                    </div>
                                    <div className="mb-3">
                                        <div className="title mb-2">
                                            <i className="mdi mdi-palette-outline"></i>
                                            <span>Tông màu</span>
                                        </div>
                                        <input
                                            className="form-control form-control-color w-100"
                                            type="color"
                                            name="tone_color"
                                            value={formState.tone_color}
                                            onChange={handleChange}
                                        />
                                    </div>
                                    <button className="btn btn-primary btn-lg w-100" type="submit" disabled={updating || !!imageError}>
                                        {updating ? "Đang cập nhật..." : "Cập nhật thông tin"}
                                    </button>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <ConfirmModal
                isOpen={isConfirmOpen}
                typeConfirm="primary"
                title="Xác nhận cập nhật"
                content="Bạn có chắc chắn muốn cập nhật thông tin quán?"
                onConfirm={handleConfirmUpdate}
                onCancel={() => setIsConfirmOpen(false)}
            />
        </React.Fragment>
    );
};

export default ShopEditInfo;
